/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { Layout, PureSection, StaticSEO } from '@core';
import { IntlWrapper, useIsMounted, useNewEntityName } from '@modules';
import { useLocation } from '@reach/router';
import { Box, jsx } from '@themed';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet';
import { AnnounceTransformToAOE } from './AnnounceTransformToAOE';
import { styles } from './index.styles';

/**
 * Extracts locale from pathname (e.g., /en/blog -> 'en', /de -> 'de', / -> 'de')
 * Note: Cannot use useLocale() here because this component wraps at the top level
 * before any IntlProvider exists.
 */
const getLocaleFromPath = (pathname: string): string => {
  const match = pathname.match(/^\/(en|fr|de)(\/|$)/);
  return match ? match[1] : 'de'; // default to 'de'
};

/**
 * Builds announcement content based on entity names
 */
const getAnnouncementContent = (oldEntityName: string, newEntityName: string) => ({
  de: {
    title: `${oldEntityName} wird AOE`,
    description: `${oldEntityName} ist seit 2024 Teil der AOE Group. Wir operieren nun unter dem Namen ${newEntityName}. Unsere Dienstleistungen finden Sie unter www.aoe.com`,
  },
  en: {
    title: `${oldEntityName} becomes AOE`,
    description: `${oldEntityName} has been part of the AOE Group since 2024. We are now operating under the name ${newEntityName}. You can find our services at www.aoe.com`,
  },
  fr: {
    title: `${oldEntityName} devient AOE`,
    description: `${oldEntityName} fait partie du groupe AOE depuis 2024. Nous opérons désormais sous le nom ${newEntityName}. Vous pouvez trouver nos services sur www.aoe.com`,
  },
});

type AnnouncementContent = ReturnType<typeof getAnnouncementContent>;

const getAnnouncementMetaTags = (
  locale: string,
  siteUrl: string,
  pathname: string,
  announcementContent: AnnouncementContent,
  newEntityName: string
) => {
  const content = announcementContent[locale as keyof AnnouncementContent] || announcementContent.en;
  const currentUrl = `${siteUrl}${pathname || '/'}`;

  return [
    { name: 'title', content: content.title },
    { name: 'description', content: content.description },
    { property: 'og:title', content: content.title },
    { property: 'og:description', content: content.description },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: currentUrl },
    { property: 'og:site_name', content: newEntityName },
    { name: 'twitter:card', content: 'summary' },
    { name: 'twitter:title', content: content.title },
    { name: 'twitter:description', content: content.description },
  ];
};

export const PageAnnouncementWrapper = ({ element }: { element: React.ReactElement }) => {
  const location = useLocation();
  const locale = getLocaleFromPath(location?.pathname || '/');
  const mounted = useIsMounted();
  const { newEntityName, oldEntityName } = useNewEntityName();

  // Refresh page on resize/rotation to recalculate layout/animations
  React.useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => window.location.reload(), 300);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      clearTimeout(debounceTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Build announcement content based on entity names
  const announcementContent = React.useMemo(() => getAnnouncementContent(oldEntityName, newEntityName), [oldEntityName, newEntityName]);

  // Get the full title for typewriter effect
  const fullTitle = announcementContent[locale as keyof typeof announcementContent]?.title || announcementContent.en.title;
  // const displayTitle = useTypewriterTitle(fullTitle, 200);

  // Get siteUrl directly from GraphQL without requiring Intl context
  const { site } = useStaticQuery<{ site: { siteMetadata: { siteUrl: string } } }>(graphql`
    query AnnouncementSiteUrl {
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const announcementMetaTags = getAnnouncementMetaTags(
    locale,
    site.siteMetadata.siteUrl,
    location?.pathname || '/',
    announcementContent,
    newEntityName
  );

  const overlayContent = (
    <IntlWrapper locale={locale as 'de' | 'en' | 'fr'} originalPath={location?.pathname || '/'}>
      {/* Backdrop to dim the background */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'primary',
          zIndex: 9999,
          maxHeight: ['100vh', '100vh', 'unset'],
          overflowY: 'auto',
          // Ensure it's positioned relative to viewport, not scroll container
          // Using transform creates a new stacking context and helps with smooth-scrollbar
          transform: 'translateZ(0)',
          willChange: 'transform',
        }}
      >
        {/* Announcement content */}
        <Box
          sx={{
            position: 'relative',
            maxWidth: '100vw',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
            section: {
              border: 'none !important',
            },
          }}
        >
          <Layout invertedHeader headerMenu={false}>
            <StaticSEO pageId='home' />
            {/* Override title and meta tags - rendered last to take precedence */}
            <Helmet>
              <title>{fullTitle}</title>
              {announcementMetaTags.map((tag, index) => (
                <meta key={index} {...tag} />
              ))}
            </Helmet>
            <PureSection sx={{ ...styles.init.section, backgroundColor: 'primary', minHeight: 'auto' }}>
              <AnnounceTransformToAOE />
            </PureSection>
          </Layout>
        </Box>
      </Box>
    </IntlWrapper>
  );

  return (
    <React.Fragment>
      {/* Render original page content normally in the background */}
      {element}

      {/* Announcement overlay on top - render via portal to escape smooth-scrollbar container */}
      {mounted && typeof document !== 'undefined' ? createPortal(overlayContent, document.body) : overlayContent}
    </React.Fragment>
  );
};
